/**
 * 房间会话接线层：副作用（Transport 生命周期、Zustand 同步、历史快照）都集中在这里，
 * 对局状态本身只放在 `useReducer` 里，绝不进 Zustand。
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { fogChess } from '@games/fog-chess'
import { createPeerJsTransport, isRoomCode, normalizeRoomCode, randomCode } from '@games/net'
import type { Transport, TransportFactory } from '@games/net'
import { MAX_CHAT_LENGTH } from '@games/protocol'
import type { GameMessage } from '@games/protocol'
import { appConfig } from '../config'
import { createRoomToken, getRoomToken } from '../platform'
import { useChat } from '../stores/chat'
import { useSession } from '../stores/session'
import { pushHistory } from './history'
import { HOST_NAME, createRoomReducer, initialRoomState } from './roomReducer'
import type { RoomState } from './roomReducer'
import { planMessage } from './routing'

type FogChessState = ReturnType<typeof fogChess.createInitialState>
export type FogChessMove = Parameters<typeof fogChess.apply>[1]

/** 对手/房主掉线后的宽限期：这段时间保留棋局，等它带同一令牌回来（D11）。 */
const RECONNECT_GRACE_MS = 30_000
/** 收到连接后等 `hello` 的上限；超时按「无令牌的新身份」处理（兼容旧客户端）。 */
const HELLO_TIMEOUT_MS = 5_000

export interface RoomSessionOptions {
  /** 注入传输层工厂（测试用）；缺省走 PeerJS */
  readonly transportFactory?: TransportFactory
  /** 注入身份令牌（测试用）；缺省从 sessionStorage 取/生成 */
  readonly roomToken?: string
}

export interface RoomSession {
  readonly state: RoomState<FogChessState, FogChessMove>
  host(): void
  join(roomCode: string): void
  cancel(): void
  leave(): void
  sendChat(text: string): void
  requestRestart(): void
  markChatRead(): void
  /**
   * 契约补充（见最终报告「契约变更申请」）：把本方走子交给房间层校验、应用并转发。
   * T5.md 的 `RoomSession` 清单漏了走子入口，而 `RoomScreen` 只拿得到 `RoomSession`。
   */
  move(move: FogChessMove): void
}

/** 两端一致性指纹；游戏模块没提供 hash 时退化为步数（对方也不会做比对）。 */
function hashOf(state: FogChessState): string {
  return typeof fogChess.hash === 'function' ? fogChess.hash(state) : String(state.moveCount)
}

export function useRoomSession(
  initialRoomCode?: string | null,
  options: RoomSessionOptions = {},
): RoomSession {
  const { transportFactory, roomToken } = options
  const roomReducer = useMemo(() => createRoomReducer<FogChessState, FogChessMove>(fogChess), [])
  const [state, dispatch] = useReducer(
    roomReducer,
    initialRoomState as RoomState<FogChessState, FogChessMove>,
  )
  const [transport, setTransport] = useState<Transport | null>(null)

  /**
   * 用 ref 镜像一份「最新状态」。
   *
   * 注意：它只在**渲染时**同步。同一次宏任务里可能连着到达好几条消息（房主先发 `move`
   * 再发 `peer-state`），那时 React 还没重渲染，所以对「必须看到本批效果」的判断
   * （例如哈希校验）要把本批动作自己先跑一遍，见下面消息处理器里的 `effective`。
   */
  const stateRef = useRef(state)
  stateRef.current = state
  const transportRef = useRef<Transport | null>(null)
  const factoryRef = useRef<TransportFactory | null>(null)
  const aliveRef = useRef(true)
  const lastHistoryRef = useRef<FogChessState | null>(null)
  const autoJoinRef = useRef(false)
  const messageHandlerRef = useRef<(from: string, message: GameMessage) => void>(() => { })
  /** 本标签页在该房间的身份令牌（D11）；未建房/未加入时为 null */
  const tokenRef = useRef<string | null>(roomToken ?? null)
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const awayPeerRef = useRef<string | null>(null)
  /** 收到但还没到「本方步数追平」的对方指纹，见下面那个 useEffect */
  const pendingPeerHashRef = useRef<{ hash: string; moveCount: number } | null>(null)
  /** 房主侧：已连接但还没等到 `hello` 的 peer → 定时器 */
  const pendingHelloRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  /** 已经对哪些 peer 发过 `hello`（同一个 peer 只发一次） */
  const helloSentRef = useRef<Set<string>>(new Set())

  const getFactory = useCallback((): TransportFactory => {
    if (transportFactory) return transportFactory
    if (!factoryRef.current) {
      // TURN 端点来自 .env 的 VITE_TURN_ENDPOINT（见 src/config.ts）；
      // 未配置时传 undefined，由 @games/net 回退到同源的 '/turn'。
      const turnEndpoint = appConfig.turnEndpoint
      factoryRef.current =
        turnEndpoint === undefined ? createPeerJsTransport() : createPeerJsTransport({ turnEndpoint })
    }
    return factoryRef.current
  }, [transportFactory])

  const syncRoomUrl = useCallback((roomCode: string) => {
    // 只改地址栏便于刷新/复制，不能触发路由跳转
    window.history.replaceState(null, '', `/fogchess?room=${encodeURIComponent(roomCode)}`)
  }, [])

  const clearTimers = useCallback(() => {
    if (graceTimerRef.current !== null) {
      clearTimeout(graceTimerRef.current)
      graceTimerRef.current = null
    }
    awayPeerRef.current = null
    for (const timer of pendingHelloRef.current.values()) clearTimeout(timer)
    pendingHelloRef.current.clear()
    helloSentRef.current.clear()
  }, [])

  const disposeTransport = useCallback(() => {
    const current = transportRef.current
    transportRef.current = null
    clearTimers()
    if (current) {
      try {
        current.close()
      } catch {
        // 对端已关闭 / 重复关闭都不算错误
      }
    }
  }, [clearTimers])

  const host = useCallback(() => {
    if (transportRef.current) return
    const code = randomCode()
    const created = getFactory().createHost({ roomCode: code })
    transportRef.current = created
    setTransport(created)
    const roomCode = created.roomCode || code
    dispatch({ type: 'host-started', roomCode, shareLink: created.shareLink })
    syncRoomUrl(roomCode)
  }, [getFactory, syncRoomUrl])

  const join = useCallback(
    (rawCode: string) => {
      if (transportRef.current) return
      const code = normalizeRoomCode(rawCode)
      if (!isRoomCode(code)) {
        dispatch({ type: 'transport-state', status: 'failed', error: '房间号格式不对，请检查后重试。' })
        return
      }
      // 身份令牌按房间号隔离（D11）：同标签页刷新后带同一令牌回来，就能拿回座位/编号
      tokenRef.current = roomToken ?? getRoomToken(code) ?? createRoomToken(code)
      const created = getFactory().createGuest({ roomCode: code })
      transportRef.current = created
      setTransport(created)
      dispatch({ type: 'join-started', roomCode: code })
    },
    [getFactory, roomToken],
  )

  /** 离开 / 取消：断开连接、清空房间与聊天，回到 `/fogchess` 的创建/加入界面。 */
  const resetToIdle = useCallback(() => {
    disposeTransport()
    setTransport(null)
    dispatch({ type: 'reset' })
    useSession.getState().reset()
    useChat.getState().clear()
    window.history.replaceState(null, '', '/fogchess')
  }, [disposeTransport])

  /** 房主侧：接入者连上后等它的 `hello`；超时就当「无令牌的新身份」放进来。 */
  const armHelloTimeout = useCallback((peerId: string) => {
    if (stateRef.current.role !== 'host') return
    if (pendingHelloRef.current.has(peerId)) return
    const timer = setTimeout(() => {
      pendingHelloRef.current.delete(peerId)
      dispatch({ type: 'hello', token: `anon-${peerId}`, peer: peerId })
    }, HELLO_TIMEOUT_MS)
    pendingHelloRef.current.set(peerId, timer)
  }, [])

  const clearHelloTimeout = useCallback((peerId: string) => {
    const timer = pendingHelloRef.current.get(peerId)
    if (timer === undefined) return
    clearTimeout(timer)
    pendingHelloRef.current.delete(peerId)
  }, [])

  /** 掉线不立刻判终局：先留宽限期保留棋局，到点才 `peer-gone`（D11）。 */
  const startGrace = useCallback((peer: string) => {
    awayPeerRef.current = peer
    if (graceTimerRef.current !== null) return
    graceTimerRef.current = setTimeout(() => {
      graceTimerRef.current = null
      const gone = awayPeerRef.current
      if (gone === null) return
      awayPeerRef.current = null
      dispatch({
        type: 'peer-gone',
        peer: gone,
        reason: stateRef.current.role === 'host' ? '对方已断开连接。' : '房主已断开连接。',
      })
    }, RECONNECT_GRACE_MS)
  }, [])

  /** 房主中继：转给「除发送者外的全部连接」（传输层不认识角色，这层自己做）。 */
  const relay = useCallback((sender: string, message: GameMessage) => {
    const active = transportRef.current
    if (!active) return
    for (const peer of active.peers) {
      if (peer === sender) continue
      active.send(peer, message)
    }
  }, [])

  const move = useCallback((next: FogChessMove) => {
    const current = stateRef.current
    // 观众没有座位，永远走不了子
    if (current.game === null || current.self === null || current.role === 'spectator') return
    if (!fogChess.isLegal(current.game, next, current.self)) return
    dispatch({ type: 'local-move', move: next })
    const active = transportRef.current
    if (!active) return
    // 必须带 by：观众无从推导执子方，房主中继时会再以自权威覆盖一次
    active.send('*', { type: 'move', from: next.from, to: next.to, by: current.self })
    const after = fogChess.apply(current.game, next, current.self)
    active.send('*', { type: 'peer-state', hash: hashOf(after), moveCount: after.moveCount })
  }, [])

  const requestRestart = useCallback(() => {
    if (stateRef.current.role === 'spectator') return
    dispatch({ type: 'restart', source: 'self' })
    transportRef.current?.send('*', { type: 'restart' })
  }, [])

  const sendChat = useCallback((text: string) => {
    const trimmed = text.trim()
    if (!trimmed) return
    const clipped = trimmed.slice(0, MAX_CHAT_LENGTH)
    useChat.getState().push({ mine: true, text: clipped })
    // 观众也能发言（D7）：署名由房主在转发时改写，这里不填
    const isHost = stateRef.current.role === 'host'
    transportRef.current?.send(
      '*',
      isHost ? { type: 'chat', text: clipped, name: HOST_NAME } : { type: 'chat', text: clipped },
    )
  }, [])

  const markChatRead = useCallback(() => {
    useChat.getState().markRead()
  }, [])

  // 处理器每次渲染重建，订阅里通过 ref 调用，避免闭包读到旧 state
  messageHandlerRef.current = (from: string, message: GameMessage) => {
    // 收到自我介绍就不必再用超时兜底了
    if (message.type === 'hello') clearHelloTimeout(from)
    // 决策全在纯函数里（可单测，见 routing.ts），这里只负责产生副作用
    const plan = planMessage(stateRef.current, from, message, (fromPos, toPos) => ({
      from: fromPos,
      to: toPos,
    }))
    for (const action of plan.actions) dispatch(action)
    if (plan.chat) {
      useChat.getState().push({ mine: false, text: plan.chat.text, name: plan.chat.name })
    }
    if (plan.relay) relay(from, plan.relay)
    // 拿到身份后立刻补局：中途进来的观众/重连回来的玩家都靠这一步看到当前局面
    if (message.type === 'welcome') transportRef.current?.send(from, { type: 'sync-request' })
    if (message.type !== 'peer-state') return
    // 收到的指纹不能马上与本地比对：`move` 与 `peer-state` 是两条独立消息，
    // React 的重渲染可能落在两者之间，此时本地局面还差一步。先记下来，
    // 等本方步数追上对方的 moveCount 再校验（这也是 peer-state 带 moveCount 的原因）。
    pendingPeerHashRef.current = { hash: message.hash, moveCount: message.moveCount }
  }

  // 步数对齐后才比对指纹；只是告警，不一致不中断对局
  useEffect(() => {
    const pending = pendingPeerHashRef.current
    if (pending === null) return
    const game = state.game
    if (game === null || game.moveCount !== pending.moveCount) return
    pendingPeerHashRef.current = null
    if (typeof fogChess.hash !== 'function') return
    const mine = fogChess.hash(game)
    if (mine !== pending.hash) {
      console.warn('[fog-chess] 双方状态不一致：', {
        mine,
        remote: pending.hash,
        remoteMoveCount: pending.moveCount,
      })
    }
  }, [state])

  // 发件箱：把「谁能进来、要补哪几步」这类纯逻辑的结果送出去（判定本身在 reducer 里）
  useEffect(() => {
    const active = transportRef.current
    if (!active || state.outbox.length === 0) return
    for (const item of state.outbox) {
      if (item.type === 'welcome') {
        active.send(item.peer, {
          type: 'welcome',
          role: item.role,
          ...(item.seat === null ? {} : { seat: item.seat }),
          name: item.name,
        })
      } else {
        active.send(item.peer, {
          type: 'sync',
          moves: item.moves.map((entry) => ({
            by: entry.by,
            from: entry.move.from,
            to: entry.move.to,
          })),
        })
      }
    }
    // 排空后清掉刚发出去的那几条
    dispatch({ type: 'outbox-flushed', count: state.outbox.length })
  }, [state.outbox])

  // 观战人数只有房主算得出来（身份表在它手上），所以由它广播给所有人（D12）
  useEffect(() => {
    if (state.role !== 'host') return
    const active = transportRef.current
    if (!active) return
    active.send('*', { type: 'audience', count: state.audienceCount })
  }, [state.role, state.audienceCount])

  useEffect(() => {
    if (!transport) return

    const announce = (peerId?: string): void => {
      // 房主自己是判定者，不需要自我介绍
      if (stateRef.current.role === 'host') return
      const token = tokenRef.current
      if (token === null) return
      const peer = peerId ?? transport.peers[0]
      if (peer === undefined || peer === '') return
      if (helloSentRef.current.has(peer)) return
      helloSentRef.current.add(peer)
      transport.send(peer, { type: 'hello', token })
    }

    const unsubscribers = [
      transport.onStateChange((nextStatus, error) => {
        const current = stateRef.current
        // 对局中断线：先给宽限期保留棋局，别把对局直接判死（D11）
        if (
          (nextStatus === 'closed' || nextStatus === 'failed') &&
          current.phase === 'playing' &&
          !current.peerAway
        ) {
          const peer = awayPeerRef.current ?? current.opponentPeer ?? ''
          dispatch({ type: 'peer-away', peer })
          startGrace(peer)
          return
        }
        dispatch({ type: 'transport-state', status: nextStatus, error })
        // 有的实现只在 data channel 打开时给 connected，不一定补发 onPeerJoin
        if (nextStatus === 'connected') announce(transport.peers[0])
      }),
      transport.onPeerJoin((peerId) => {
        armHelloTimeout(peerId)
        announce(peerId)
      }),
      transport.onPeerLeave((peerId) => {
        if (stateRef.current.phase !== 'playing') return
        clearHelloTimeout(peerId)
        dispatch({ type: 'peer-away', peer: peerId })
        startGrace(peerId)
      }),
      transport.onMessage((from, message) => messageHandlerRef.current(from, message)),
    ]
    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe()
    }
  }, [transport, armHelloTimeout, clearHelloTimeout, startGrace])

  // 每一手（本方或对方）落一步历史；只在棋盘真的前进时写
  useEffect(() => {
    const current = state.game
    if (!current || current.moveCount === 0) return
    if (lastHistoryRef.current === current) return
    lastHistoryRef.current = current
    pushHistory(current)
  }, [state.game])

  // 连接态同步进会话 store，供 ConnectionBadge 等通用组件消费；对局状态不进
  useEffect(() => {
    const session = useSession.getState()
    session.setStatus(state.status, state.error)
    session.setRoom({ roomCode: state.roomCode, shareLink: state.shareLink })
    // store 里放的是传输层 peer id，不是对局座位（TC-1）
    session.setPeers(transport ? transport.selfId : null, state.opponentPeer)
  }, [state.status, state.error, state.roomCode, state.shareLink, state.opponentPeer, transport])

  // 深链 ?room=CODE：自动以加入方身份连接一次，StrictMode 下也只触发一次
  useEffect(() => {
    if (autoJoinRef.current) return
    autoJoinRef.current = true
    if (!initialRoomCode) return
    const code = normalizeRoomCode(initialRoomCode)
    if (!isRoomCode(code)) {
      dispatch({ type: 'transport-state', status: 'failed', error: '房间号格式不对，请检查后重试。' })
      return
    }
    join(code)
  }, [initialRoomCode, join])

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      // StrictMode 会「挂载 → 卸载 → 再挂载」；把释放推迟到微任务，重新挂载就取消，
      // 避免误杀刚建立的连接（异步建连尚未完成时尤其重要）
      queueMicrotask(() => {
        if (aliveRef.current) return
        disposeTransport()
        useSession.getState().reset()
        useChat.getState().clear()
      })
    }
  }, [disposeTransport])

  return {
    state,
    host,
    join,
    cancel: resetToIdle,
    leave: resetToIdle,
    sendChat,
    requestRestart,
    markChatRead,
    move,
  }
}
